import { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, CheckCircle, MapPin, Play, Square, Truck } from 'lucide-react';
import { useNavigate } from 'react-router';
import { DriverBottomNav } from '../components/DriverBottomNav';
import { getStoredDriverMobileUser } from '../utils/driverMobileSession';
import { emptyReturnService } from '../services/emptyReturnService';
import { toast } from 'sonner';
import { isInternalDriverUser } from '../utils/userRoles';

type Responsible = { id: string; name: string; role: string };
type EmptyReturnRecord = {
  id: string;
  responsibleName: string;
  vehiclePlate: string;
  trailerPlate: string;
  status: string;
  startAt: string;
  startLatitude: number | null;
  startLongitude: number | null;
  startAddress: string;
  endAt: string | null;
  endLatitude: number | null;
  endLongitude: number | null;
  endAddress: string;
};

export function DriverMobileEmptyReturn() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [records, setRecords] = useState<EmptyReturnRecord[]>([]);
  const [responsibles, setResponsibles] = useState<Responsible[]>([]);
  const [selectedResponsibleId, setSelectedResponsibleId] = useState('');
  const [saving, setSaving] = useState(false);

  const activeRecord = useMemo(() => records.find((item) => item.status === 'active') || null, [records]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [recordsResponse, responsiblesResponse] = await Promise.all([
        emptyReturnService.getMyRecords(),
        emptyReturnService.getResponsibles(),
      ]);

      const nextRecords = Array.isArray((recordsResponse as any)?.data) ? (recordsResponse as any).data : [];
      const nextResponsibles = Array.isArray((responsiblesResponse as any)?.data) ? (responsiblesResponse as any).data : [];
      setRecords(nextRecords);
      setResponsibles(nextResponsibles);
      if (!selectedResponsibleId && nextResponsibles[0]?.id) {
        setSelectedResponsibleId(nextResponsibles[0].id);
      }
    } catch (error: any) {
      console.error('Load empty return data failed:', error);
      toast.error(error?.message || 'Boş dönüş verileri yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const storedUser = getStoredDriverMobileUser<any>();
    if (!storedUser) {
      navigate('/driver-mobile-auth', { replace: true });
      return;
    }
    if (!isInternalDriverUser(storedUser)) {
      navigate('/driver-mobile-home', { replace: true });
      return;
    }
    loadData();
  }, [navigate]);

  const handleStart = async () => {
    if (!selectedResponsibleId) {
      toast.error('Operasyon sorumlusu seçmelisiniz');
      return;
    }
    try {
      setSaving(true);
      await emptyReturnService.start(selectedResponsibleId);
      toast.success('Boş dönüş başlatıldı');
      await loadData();
    } catch (error: any) {
      console.error('Start empty return failed:', error);
      toast.error(error?.message || 'Boş dönüş başlatılamadı');
    } finally {
      setSaving(false);
    }
  };

  const handleFinish = async () => {
    if (!activeRecord) return;
    try {
      setSaving(true);
      await emptyReturnService.finish(activeRecord.id);
      toast.success('Boş dönüş tamamlandı');
      await loadData();
    } catch (error: any) {
      console.error('Finish empty return failed:', error);
      toast.error(error?.message || 'Boş dönüş tamamlanamadı');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="safe-area-page min-h-dvh bg-gray-50 dark:bg-[#0b0b0b]">
      <div className="sticky top-0 z-10 border-b border-slate-200 bg-white p-4 pb-6 shadow-sm dark:border-slate-800 dark:bg-gradient-to-r dark:from-blue-900 dark:to-blue-800 dark:text-white dark:shadow-lg">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/driver-mobile-home')} className="inline-flex items-center gap-2 rounded-xl bg-slate-100 px-3 py-2 text-slate-900 ring-1 ring-slate-200 transition-colors hover:bg-slate-200 dark:bg-white/15 dark:text-white dark:ring-white/20 dark:hover:bg-white/20">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Boş Dönüş</h1>
            <p className="text-sm text-slate-500 dark:text-blue-200">Boş dönüş başlangıç ve bitiş kayıtlarını yönetin</p>
          </div>
        </div>
      </div>

      <div className="p-4 space-y-4">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-2xl bg-blue-100 text-blue-700 flex items-center justify-center">
              <Truck className="w-5 h-5" />
            </div>
            <div>
              <p className="text-sm font-semibold text-slate-900">Boş Dönüş Yönetimi</p>
              <p className="text-xs text-slate-500 mt-1">Koordinatlar araç konumundan otomatik alınır</p>
            </div>
          </div>

          {!activeRecord ? (
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Operasyon Sorumlusu</label>
                <select value={selectedResponsibleId} onChange={(e) => setSelectedResponsibleId(e.target.value)} className="w-full rounded-xl border border-slate-300 px-4 py-3 bg-white text-sm">
                  <option value="">Seçiniz</option>
                  {responsibles.map((item) => (
                    <option key={item.id} value={item.id}>{item.name} ({item.role})</option>
                  ))}
                </select>
              </div>
              <button onClick={handleStart} disabled={saving || !selectedResponsibleId} className="w-full rounded-xl bg-blue-600 px-4 py-3 text-sm font-semibold text-white disabled:opacity-50 inline-flex items-center justify-center gap-2">
                <Play className="w-4 h-4" /> {saving ? 'Başlatılıyor...' : 'Boş Dönüş Başlat'}
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-amber-800">Aktif Boş Dönüş</p>
                    <p className="text-xs text-amber-700 mt-1">{new Date(activeRecord.startAt).toLocaleString('tr-TR')}</p>
                  </div>
                  <span className="px-3 py-1 rounded-full bg-amber-100 text-amber-700 text-xs font-semibold">Devam Ediyor</span>
                </div>
                <div className="mt-3 text-sm text-slate-700 space-y-2">
                  <div><strong>Sorumlu:</strong> {activeRecord.responsibleName}</div>
                  <div><strong>Çekici / Dorse:</strong> {activeRecord.vehiclePlate || '-'} / {activeRecord.trailerPlate || '-'}</div>
                  <div><strong>Başlangıç:</strong> {activeRecord.startAddress || '-'}</div>
                  <div className="text-xs text-slate-500">{activeRecord.startLatitude ?? '-'}, {activeRecord.startLongitude ?? '-'}</div>
                </div>
              </div>
              <button onClick={handleFinish} disabled={saving} className="w-full rounded-xl bg-emerald-600 px-4 py-3 text-sm font-semibold text-white disabled:opacity-50 inline-flex items-center justify-center gap-2">
                <Square className="w-4 h-4" /> {saving ? 'Bitiriliyor...' : 'Boş Dönüş Bitir'}
              </button>
            </div>
          )}
        </div>

        <div className="space-y-3">
          <h2 className="text-lg font-bold text-gray-900">Geçmiş Kayıtlar</h2>
          {loading ? (
            <div className="bg-white rounded-2xl border border-slate-200 p-6 text-center text-sm text-slate-500">Yükleniyor...</div>
          ) : records.length === 0 ? (
            <div className="bg-white rounded-2xl border border-slate-200 p-6 text-center text-sm text-slate-500">Boş dönüş kaydı yok</div>
          ) : records.map((record) => (
            <div key={record.id} className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-slate-900">{record.vehiclePlate || '-'} / {record.trailerPlate || '-'}</p>
                  <p className="text-xs text-slate-500 mt-1">Sorumlu: {record.responsibleName}</p>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${record.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>{record.status === 'completed' ? 'Tamamlandı' : 'Aktif'}</span>
              </div>
              <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
                <div className="rounded-xl bg-emerald-50 border border-emerald-100 p-3">
                  <p className="text-xs font-medium text-emerald-700 mb-2 flex items-center gap-2"><MapPin className="w-4 h-4" /> Başlangıç</p>
                  <p className="text-sm font-medium text-slate-900">{record.startAddress || '-'}</p>
                  <p className="text-xs text-slate-500 mt-1">{new Date(record.startAt).toLocaleString('tr-TR')}</p>
                  <p className="text-xs text-slate-500 mt-1">{record.startLatitude ?? '-'}, {record.startLongitude ?? '-'}</p>
                </div>
                <div className="rounded-xl bg-rose-50 border border-rose-100 p-3">
                  <p className="text-xs font-medium text-rose-700 mb-2 flex items-center gap-2"><CheckCircle className="w-4 h-4" /> Bitiş</p>
                  <p className="text-sm font-medium text-slate-900">{record.endAddress || (record.status === 'active' ? 'Devam ediyor' : '-')}</p>
                  <p className="text-xs text-slate-500 mt-1">{record.endAt ? new Date(record.endAt).toLocaleString('tr-TR') : '-'}</p>
                  <p className="text-xs text-slate-500 mt-1">{record.endLatitude ?? '-'}, {record.endLongitude ?? '-'}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <DriverBottomNav />
    </div>
  );
}
